/**
 * AdminJotAuditPage Component
 *
 * Admin page for JotAudit dashboard with cross-organization visibility
 * Tabs: Overview | Organizations | Providers | Notes
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Assessment as OverviewIcon,
  Business as OrganizationsIcon,
  Description as NotesIcon,
  People as ProvidersIcon,
} from '@mui/icons-material';
import { Box, Container, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AdminDashboardOverview } from './components/admin/AdminDashboardOverview';
import { AdminNoteAuditsList } from './components/admin/AdminNoteAuditsList';
import { AdminProvidersList } from './components/admin/AdminProvidersList';
import { OrganizationsList } from './components/organizations/OrganizationsList';
import { ADMIN_TAB_CONFIG } from './constants';
import type { AdminJotAuditTabType } from './types';

const DEFAULT_TAB: AdminJotAuditTabType = 'overview';

interface TabPanelProps {
  children?: React.ReactNode;
  value: AdminJotAuditTabType;
  activeTab: AdminJotAuditTabType;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

export const AdminJotAuditPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Organization filter state
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined);
  const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null);

  // Get current tab from URL or default
  const currentTab = useMemo<AdminJotAuditTabType>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'organizations', 'providers', 'notes'].includes(tab)) {
      return tab as AdminJotAuditTabType;
    }
    return DEFAULT_TAB;
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: AdminJotAuditTabType) => {
      setSearchParams({ tab: newValue });
    },
    [setSearchParams],
  );

  // Handle organization selection from organizations list
  const handleOrganizationSelect = useCallback(
    (organizationId: string, organizationName: string | null) => {
      setSelectedOrgId(organizationId);
      setSelectedOrgName(organizationName);
      // Navigate to providers tab to show that org's providers
      setSearchParams({ tab: 'providers' });
    },
    [setSearchParams],
  );

  // Clear organization filter
  const handleClearOrgFilter = useCallback(() => {
    setSelectedOrgId(undefined);
    setSelectedOrgName(null);
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          JotAudit Admin
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          Review note quality, compliance, and billing accuracy across all organizations
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: '12px 12px 0 0',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.grey[200]}`,
          borderBottomColor: 'divider',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 56,
              fontSize: '0.9375rem',
              fontWeight: 500,
              gap: 1,
            },
          }}
        >
          <Tab
            value={ADMIN_TAB_CONFIG.overview.value}
            label={ADMIN_TAB_CONFIG.overview.label}
            icon={<OverviewIcon />}
            iconPosition="start"
          />
          <Tab
            value={ADMIN_TAB_CONFIG.organizations.value}
            label={ADMIN_TAB_CONFIG.organizations.label}
            icon={<OrganizationsIcon />}
            iconPosition="start"
          />
          <Tab
            value={ADMIN_TAB_CONFIG.providers.value}
            label={ADMIN_TAB_CONFIG.providers.label}
            icon={<ProvidersIcon />}
            iconPosition="start"
          />
          <Tab
            value={ADMIN_TAB_CONFIG.notes.value}
            label={ADMIN_TAB_CONFIG.notes.label}
            icon={<NotesIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          bgcolor: 'background.paper',
          minHeight: 400,
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Overview Tab */}
          <TabPanel value="overview" activeTab={currentTab}>
            <AdminDashboardOverview />
          </TabPanel>

          {/* Organizations Tab */}
          <TabPanel value="organizations" activeTab={currentTab}>
            <OrganizationsList onOrganizationSelect={handleOrganizationSelect} />
          </TabPanel>

          {/* Providers Tab */}
          <TabPanel value="providers" activeTab={currentTab}>
            <AdminProvidersList
              organizationId={selectedOrgId}
              organizationName={selectedOrgName}
              onClearFilter={handleClearOrgFilter}
            />
          </TabPanel>

          {/* Notes Tab */}
          <TabPanel value="notes" activeTab={currentTab}>
            <AdminNoteAuditsList
              organizationId={selectedOrgId}
              organizationName={selectedOrgName}
              onClearFilter={handleClearOrgFilter}
            />
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminJotAuditPage;

